/**
 * AskAudiaHelpButton - Context-aware Audia help button for reader pages
 * 
 * Provides platform help with awareness of where the user is in the app.
 * Uses the same helpdesk knowledge base as the FAQ page.
 * Supports clickable navigation links in responses.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, X, Loader2 } from 'lucide-react';
import api from '../lib/api';

// Navigation link mappings for common pages
const PAGE_LINKS = {
  'reader home': '/home',
  'reader homepage': '/home',
  'homepage': '/home',
  'home page': '/home',
  'my library': '/reader/my-library',
  'library': '/reader/my-library',
  'pricing': '/pricing',
  'pricing page': '/pricing',
  'buy credits': '/pricing',
  'credit bundles': '/pricing',
  'subscriptions': '/pricing',
  'faq': '/faq',
  'frequently asked questions': '/faq',
  'contact': '/contact',
  'contact us': '/contact',
  'contact page': '/contact',
  'support': '/contact',
  'about': '/about',
  'about page': '/about',
  'about newaudia': '/about',
  'sign up': '/login?mode=register',
  'register': '/login?mode=register',
  'create account': '/login?mode=register',
  'sign in': '/login',
  'login': '/login',
};

// Context-specific welcome messages and tips
const CONTEXT_CONFIG = {
  'reader-home': {
    welcomeMessage: "Hi! I'm Audia, and I see you're on your Reader Homepage. I can help you discover new books, understand how to use your library, learn about Reader Credits, or answer any questions about using NewAudia. What would you like to know?",
    tips: [
      "How do I find new books?",
      "What are Reader Credits?",
      "How do I start listening?"
    ],
    contextHint: "User is on the Reader Homepage - they can browse books, see recommendations, and access their library from here."
  },
  'my-library': {
    welcomeMessage: "Hi! I'm Audia, and I see you're in your Library. I can help you understand how to manage your books, continue listening where you left off, use the audio player, or chat with me about specific books. What would you like help with?",
    tips: [
      "How do I continue listening?",
      "How do I use Audia while reading?",
      "How do I remove a book?"
    ],
    contextHint: "User is in their My Library page - they can see their saved books, resume listening, and manage their collection."
  },
  'help-center': {
    welcomeMessage: "Hi! I'm Audia, and I'm here to help you navigate the Help Center. If you can't find what you're looking for in the articles, just ask me! I can answer questions about using NewAudia, explain features, or guide you to the right resource.",
    tips: [
      "How do Reader Credits work?",
      "How do I publish my book?",
      "How do I contact support?"
    ],
    contextHint: "User is in the Help Center - they are looking for help documentation and may need guidance to specific features or support."
  },
  'default': {
    welcomeMessage: "Hi! I'm Audia, your NewAudia guide. I can help you understand how to use the platform, navigate features, get started as a reader or author, and answer questions about how things work. What would you like to know?",
    tips: [
      "How do I get started?",
      "What are Reader Credits?",
      "How do I publish a book?"
    ],
    contextHint: ""
  }
};

const AskAudiaHelpButton = ({ context = 'default', className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const config = CONTEXT_CONFIG[context] || CONTEXT_CONFIG['default'];
  
  return (
    <>
      {/* Help Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`group flex items-center gap-3 bg-gradient-to-r from-purple-600/90 to-indigo-600/90 hover:from-purple-500 hover:to-indigo-500 backdrop-blur-sm border border-purple-400/30 hover:border-purple-300/50 rounded-2xl px-4 py-2.5 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl hover:shadow-purple-500/20 ${className}`}
        data-testid={`ask-audia-help-btn-${context}`}
      >
        <div className="relative flex-shrink-0">
          <img 
            src="/audia-avatar.jpg" 
            alt="Audia" 
            className="w-10 h-10 rounded-full border-2 border-white/40 group-hover:border-white/70 transition-colors object-cover"
          />
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-purple-600 animate-pulse"></span>
        </div>
        <div className="text-left">
          <p className="text-white font-semibold text-sm">Ask Audia</p>
          <p className="text-white/70 text-xs">Get Help</p>
        </div>
      </button>
      
      {/* Modal */}
      <AskAudiaHelpModal 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)} 
        config={config}
        context={context}
      />
    </>
  );
};

// Component to render message with clickable links
const MessageWithLinks = ({ content, onLinkClick }) => {
  const renderContent = () => {
    let processedContent = content;
    const links = [];
    
    Object.entries(PAGE_LINKS).forEach(([phrase, path]) => {
      const regex = new RegExp(`\\b(${phrase})\\b`, 'gi');
      if (regex.test(processedContent)) {
        links.push({ phrase, path });
      }
    });
    
    if (links.length === 0) {
      return <span className="whitespace-pre-wrap">{content}</span>;
    }
    
    const linkPhrases = links.map(l => l.phrase).join('|');
    const splitRegex = new RegExp(`(\\b(?:${linkPhrases})\\b)`, 'gi');
    const parts = content.split(splitRegex);
    
    return parts.map((part, index) => {
      const lowerPart = part.toLowerCase();
      const matchedLink = links.find(l => l.phrase.toLowerCase() === lowerPart);
      
      if (matchedLink) {
        return (
          <button
            key={index}
            onClick={() => onLinkClick(matchedLink.path)}
            className="text-purple-400 hover:text-purple-300 underline underline-offset-2 font-medium transition-colors"
          >
            {part}
          </button>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };
  
  return <p className="text-sm leading-relaxed">{renderContent()}</p>;
};

const AskAudiaHelpModal = ({ isOpen, onClose, config, context }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    { role: 'assistant', content: config.welcomeMessage }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const lastAssistantRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to top of latest assistant response, or bottom for user messages
  useEffect(() => {
    if (messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role === 'assistant' && lastAssistantRef.current) {
      lastAssistantRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([{ role: 'assistant', content: config.welcomeMessage }]);
      setInput('');
      setSuggestions([]);
    }
  }, [isOpen, config.welcomeMessage]);

  const handleLinkClick = (path) => {
    onClose();
    navigate(path);
  };

  const sendQuestion = async (question) => {
    if (!question.trim() || isLoading) return;

    setInput('');
    setSuggestions([]);
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setIsLoading(true);

    try {
      const response = await api.post('/audia/faq-assistant', {
        message: question,
        conversationHistory: messages.slice(-6),
        context: context,
        contextHint: config.contextHint
      });

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response || "I'm sorry, I couldn't process that request. Please try again."
      }]);

      if (response.data.suggestions?.length > 0) {
        setSuggestions(response.data.suggestions);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Help Assistant error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again in a moment."
      }]);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    sendQuestion(input.trim());
  };

  const handleSuggestionTap = (question) => {
    if (isLoading) return;
    sendQuestion(question);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full sm:max-w-lg h-[100dvh] sm:h-auto sm:max-h-[85vh] bg-gradient-to-br from-slate-900 to-slate-950 border-0 sm:border sm:border-purple-500/30 sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="relative">
              <img 
                src="/audia-avatar.jpg" 
                alt="Audia" 
                className="w-10 h-10 rounded-full border-2 border-purple-500/50 object-cover"
              />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-slate-900"></span>
            </div>
            <div>
              <h3 className="text-white font-semibold">Ask Audia</h3>
              <p className="text-slate-400 text-xs">Powered by NewAudia AI</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
            data-testid={`close-help-audia-${context}`}
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              ref={msg.role === 'assistant' && idx === messages.length - 1 ? lastAssistantRef : null}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-purple-600 text-white rounded-br-md'
                    : 'bg-slate-800 text-slate-200 rounded-bl-md border border-slate-700'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <MessageWithLinks content={msg.content} onLinkClick={handleLinkClick} />
                ) : (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-slate-800 text-slate-200 rounded-2xl rounded-bl-md px-4 py-3 border border-slate-700">
                <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestion Chips — show after responses OR initial tips at start */}
        {suggestions.length > 0 && !isLoading ? (
          <div data-testid={`help-audia-suggestions-${context}`} className="px-3 pt-2 pb-1 border-t border-slate-700/50 bg-slate-900/60 flex-shrink-0">
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((q, idx) => (
                <button
                  key={idx}
                  data-testid={`help-audia-suggestion-${idx}`}
                  onClick={() => handleSuggestionTap(q)}
                  className="px-2.5 py-1 sm:px-3 sm:py-1.5 text-[11px] sm:text-xs text-slate-200 bg-slate-800 border border-slate-600/60 rounded-full hover:bg-purple-900/40 hover:border-purple-500/50 hover:text-purple-300 active:scale-95 transition-all duration-150 cursor-pointer leading-snug"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : messages.length === 1 ? (
          <div className="px-4 pb-2 flex-shrink-0">
            <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
            <div className="flex flex-wrap gap-1.5">
              {config.tips.map((tip, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionTap(tip)}
                  data-testid={`help-audia-tip-${idx}`}
                  className="px-2.5 py-1 sm:px-3 sm:py-1.5 text-[11px] sm:text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full border border-slate-700 transition-colors"
                >
                  {tip}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-4 border-t border-slate-700/50 bg-slate-900/50 flex-shrink-0">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything about NewAudia..."
              className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
              disabled={isLoading}
              data-testid={`help-audia-input-${context}`}
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl px-4 py-2.5 disabled:opacity-50 transition-colors"
              data-testid={`help-audia-send-${context}`}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </form>

        {/* Close Button */}
        <div className="px-4 pb-4 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full bg-slate-800/50 hover:bg-slate-800 text-gray-400 hover:text-white border border-slate-700/50 font-bold py-2.5 rounded-xl transition-colors"
            data-testid={`help-audia-close-${context}`}
          >
            Close Chat
          </button>
        </div>
      </div>
    </div>
  );
};

export { AskAudiaHelpModal, CONTEXT_CONFIG };
export default AskAudiaHelpButton;
